import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/cn.ts';

/*
 * The Wundo mark: a navy rounded square with the green "W" glyph.
 * The glyph is drawn at its authored scale and squashed by the transform —
 * that non-uniform scale is the mark, not a mistake, so it is never "fixed".
 */
const logoVariants = cva('block shrink-0 overflow-hidden', {
  variants: {
    size: {
      md: 'size-8.5 rounded-sm shadow-hover',
      lg: 'size-10 rounded-md shadow-raise',
    },
  },
  defaultVariants: {
    size: 'lg',
  },
});

export type LogoVariants = VariantProps<typeof logoVariants>;

export interface LogoProps extends LogoVariants {
  className?: string;
  title?: string;
  /**
   * A word on a green band across the foot of the tile, naming an app beside
   * the main one — the admin console's "ADMIN". The W moves up to make room,
   * exactly as on that app's home-screen icon.
   */
  band?: string;
}

export function Logo({ className, size, title = 'Wundo', band }: LogoProps) {
  return (
    <svg
      data-slot='logo'
      viewBox='0 0 48 48'
      role='img'
      aria-label={title}
      className={cn(logoVariants({ size }), className)}
    >
      <rect width='48' height='48' rx='13' className='fill-navy' />
      <g
        transform={`translate(11 ${band ? 10.6 : 15.2}) scale(0.542 0.365)`}
        className='fill-green'
      >
        <path d='M39.8 48.3 28.7 48 24 17 19.3 47.4 7.9 48.3-0.2 2.8 6-0.3 6.6 0.6 13.1 37.7 14.1 35.7 18.2 6.1 29.2 5.9 34.5 37.7 41.4 0 47.6 2 47.8 5.6Z' />
      </g>
      {band && (
        <>
          {/* Its lower corners are the tile's own (r 13), so nothing needs clipping. */}
          <path
            d='M0 35H48A13 13 0 0 1 35 48H13A13 13 0 0 1 0 35Z'
            className='fill-green'
          />
          <text
            x='24.3'
            y='44.4'
            textAnchor='middle'
            fontSize='8.2'
            fontWeight='700'
            letterSpacing='0.6'
            className='fill-navy font-sans'
          >
            {band}
          </text>
        </>
      )}
    </svg>
  );
}

export { logoVariants };
